import { RoleType } from 'api-user-client'
import { equals } from 'ramda'
import { useQuery } from 'react-query'
import { UseQueryResult } from 'react-query/types/react/types'
import { useSelector } from 'react-redux'

import { fetchInvoicesJson } from '~shared-components/api/invoice'
import { fetchOrdersJson } from '~shared-components/api/orders'
import { fetchProducts } from '~shared-components/api/product'
import { fetchQuotesJson } from '~shared-components/api/quotes'
import { fetchUsers } from '~shared-components/api/user-service'
import {
  SharedQueryOptions,
  useLoggedInUser,
} from '~shared-components/hooks/useLoggedInUser'
import { Product } from '~shared-components/model'
import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { LoginSelector } from '~shared-components/redux/selectors/login.selector'
import {
  TransformedInvoice,
  TransformedListOrder,
  TransformedQuote,
  TransformedUser,
} from '~shared-components/transforms'
import { QueryCacheKey } from '~shared-components/utils/enums'

type SearchType = 'orders' | 'invoices' | 'products' | 'quotes' | 'users'

export interface UseSearchParams extends Partial<Record<SearchType, boolean>> {
  page?: number
  pageSize?: number
  search?: string
}

export type UseSearchResultOptions = Omit<UseSearchParams, 'page' | 'pageSize'>

export interface UseSearchData {
  invoices: {
    invoices: TransformedInvoice[]
    total: number
    totalPages: number
  }
  orders: {
    orders: TransformedListOrder[]
    total: number
    totalPages: number
  }
  products: {
    packagingTypes: Record<string, string> | undefined
    products: Product[]
    total: number
    totalPages: number
  }
  quotes: {
    quotes: TransformedQuote[]
    total: number
    totalPages: number
  }
  search: string
  users: {
    total: number
    users: TransformedUser[]
  }
}

export const SEARCH_MIN_CHARACTERS = 3

export const useSearch = ({
  invoices: showInvoices,
  orders: showOrders,
  page = 1,
  pageSize = 10,
  products: showProducts,
  quotes: showQuotes,
  search,
  users: showUsers,
}: UseSearchParams): UseQueryResult<UseSearchData> => {
  const { user } = useLoggedInUser()

  // For users search: UserAdmins can only search through users that are in the same customer contexts as they are
  const userCustomerContextIds = user?.isRole(RoleType.UserAdmin)
    ? user?.customers
        ?.filter(({ role }) => role === RoleType.UserAdmin)
        .map(({ customerContextId }) => customerContextId)
    : undefined

  // For users search: Roles above UserAdmins can search through all users in their sales groups
  const userSalesgroupIds = user?.isRoleAbove(RoleType.UserAdmin)
    ? user?.salesGroups?.map(({ id }) => id)
    : undefined

  const apiBaseUrl = useSelector(ConfigSelector.apiBaseUrl)
  const customerContextId = useSelector(LoginSelector.selectedCustomerContextId)
  const salesGroupId = useSelector(LoginSelector.selectedSalesGroupId)
  const shipToLocationId = useSelector(LoginSelector.selectedLocationId)

  const defaultSort = 'relevance'

  return useQuery(
    [QueryCacheKey.Search, search, customerContextId],
    async () => {
      const searchProducts = showProducts
        ? fetchProducts({
            apiBaseUrl,
            customerContextId,
            filters: {
              searchQuery: search,
              sort: defaultSort,
            },
            limit: pageSize,
            offset: page,
            salesGroupId,
            shipToLocationId,
          }).then(({ error, packagingTypes, products, total }) => {
            if (error) {
              throw new Error('Unable to fetch products')
            }
            return {
              packagingTypes,
              products,
              total,
              totalPages: Math.ceil(total / pageSize),
            }
          })
        : Promise.resolve({
            packagingTypes: {},
            products: [],
            total: 0,
            totalPages: 0,
          })

      const searchOrders = showOrders
        ? fetchOrdersJson({
            apiBaseUrl,
            customerContextId,
            filters: {
              searchQuery: search,
              sort: defaultSort,
            },
            include:
              'customerContext,user,order_lines.product.customer_context_products',
            limit: pageSize,
            offset: page,
            salesGroupId,
          }).then(({ error, orders, total }) => {
            if (error) {
              throw new Error('Unable to fetch orders')
            }
            return {
              orders,
              total,
              totalPages: Math.ceil(total / pageSize),
            }
          })
        : Promise.resolve({ orders: [], total: 0, totalPages: 0 })

      const searchInvoices = showInvoices
        ? fetchInvoicesJson({
            apiBaseUrl,
            customerContextId,
            filters: {
              searchQuery: search,
              sort: defaultSort,
            },
            include: 'payment,delivery.order.customerContext',
            limit: pageSize,
            offset: page,
            salesGroupId,
          }).then(({ error, invoices, total }) => {
            if (error) {
              throw new Error('Unable to fetch invoices')
            }
            return {
              invoices,
              total,
              totalPages: Math.ceil(total / pageSize),
            }
          })
        : Promise.resolve({ invoices: [], total: 0, totalPages: 0 })

      const searchQuotes = showQuotes
        ? fetchQuotesJson({
            apiBaseUrl,
            customerContextId,
            filters: {
              searchQuery: search,
              sort: defaultSort,
            },
            include:
              'soldToQuote,shipToQuote,billToQuote,quoteLines.product.customerContextProducts',
            limit: pageSize,
            offset: page,
            salesGroupId,
          }).then(({ error, quotes, total }) => {
            if (error) {
              throw new Error('Unable to fetch invoices')
            }
            return {
              quotes,
              total,
              totalPages: Math.ceil(total / pageSize),
            }
          })
        : Promise.resolve({ quotes: [], total: 0, totalPages: 0 })

      const searchUsers = showUsers
        ? fetchUsers({
            customerContextIds: userCustomerContextIds,
            filters: {
              searchQuery: search,
              sort: defaultSort,
            },
            pageNumber: page,
            pageSize,
            salesGroupIds: userSalesgroupIds,
          })
        : Promise.resolve({ total: 0, totalPages: 0, users: [] })

      const [products, orders, invoices, quotes, users] = await Promise.all([
        searchProducts,
        searchOrders,
        searchInvoices,
        searchQuotes,
        searchUsers,
      ])

      return { invoices, orders, products, quotes, users }
    },
    {
      ...SharedQueryOptions,
      enabled: (search ?? '').length >= SEARCH_MIN_CHARACTERS,
      isDataEqual: (p1, p2) => equals(p1, p2),
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000,
      // Disables the checking if the JSON response is equal, due to the response having looping references -_-;
      // So instead we use our own isDataEqual
      structuralSharing: false,
    },
  )
}
